import { useCallback, useEffect, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { ExtensionsScanResult } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { enqueueFixExtensions, scanExtensions } from './extensions-api';

/**
 * Modale « Corriger les extensions » (toute la bibliothèque).
 *
 * Renomme les fichiers dont l'extension ne correspond pas au type RÉEL détecté (un vrai
 * JPEG nommé `.png`, un WebP nommé `.jpg`, un WEBM nommé `.mp4`…) vers leur extension
 * canonique. Le CONTENU est inchangé (simple renommage). À l'ouverture, un SCAN (lecture)
 * compte les fichiers concernés et en montre un échantillon ; « Corriger » enfile le job
 * `rename_extensions` (progression suivie dans la cloche).
 *
 * Boîte de dialogue accessible : `role="dialog"` + `aria-modal`, focus initial/restauré,
 * verrou de défilement, Échap ferme. Le caller l'enveloppe dans un `<Portal>`.
 */
interface FixExtensionsModalProps {
  libraryId: string;
  onClose: () => void;
  /** Appelé après un enfilement réussi (p.ex. rafraîchir la galerie). */
  onQueued?: () => void;
}

type ScanState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; result: ExtensionsScanResult };

export function FixExtensionsModal({
  libraryId,
  onClose,
  onQueued,
}: FixExtensionsModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [scan, setScan] = useState<ScanState>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  useModalA11y({ dialogRef, initialFocusRef: closeRef, onEscape: !busy ? onClose : undefined });
  const [fixError, setFixError] = useState<'writeDisabled' | 'generic' | null>(null);
  const [done, setDone] = useState(false);

  // Verrou de défilement + focus initial + restauration.

  // Échap ferme (sauf pendant l'appel réseau).

  const runScan = useCallback(async () => {
    setScan({ status: 'loading' });
    try {
      const result = await scanExtensions(libraryId);
      setScan({ status: 'ready', result });
    } catch {
      setScan({ status: 'error' });
    }
  }, [libraryId]);

  // Scan automatique à l'ouverture (lecture seule).
  useEffect(() => {
    void runScan();
  }, [runScan]);

  const onSubmit = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setFixError(null);
    try {
      await enqueueFixExtensions(libraryId, { dryRun: false });
      setDone(true);
      onQueued?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setFixError('writeDisabled');
      } else {
        setFixError('generic');
      }
    } finally {
      setBusy(false);
    }
  }, [busy, libraryId, onQueued]);

  const total = scan.status === 'ready' ? scan.result.total : 0;
  const canFix = scan.status === 'ready' && total > 0 && !done;

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('fixExtensions.title')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <TagIcon className="h-6 w-6 text-brand" aria-hidden="true" />
            {t('fixExtensions.title')}
          </h2>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            disabled={busy}
            title={t('fixExtensions.close')}
            aria-label={t('fixExtensions.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {done ? (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
            >
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('fixExtensions.queued')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">{t('fixExtensions.intro')}</p>
              <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
                <InformationCircleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                {t('fixExtensions.note')}
              </p>

              {scan.status === 'loading' && (
                <p role="status" className="inline-flex items-center gap-2 text-sm text-muted">
                  <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                  {t('fixExtensions.scanning')}
                </p>
              )}

              {scan.status === 'error' && (
                <div className="flex flex-col gap-2">
                  <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {t('fixExtensions.scanError')}
                  </p>
                  <button
                    type="button"
                    onClick={() => void runScan()}
                    className="self-start rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {t('fixExtensions.retry')}
                  </button>
                </div>
              )}

              {scan.status === 'ready' && total === 0 && (
                <p className="rounded-md bg-surface-2 p-3 text-sm text-fg">
                  {t('fixExtensions.none')}
                </p>
              )}

              {scan.status === 'ready' && total > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-fg">
                    {interpolate(t('fixExtensions.count'), { count: total }, locale)}
                  </p>
                  <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
                    {scan.result.sample.map((r) => (
                      <li key={r.from} className="flex items-center gap-2 px-3 py-1.5">
                        <span className="min-w-0 flex-1 truncate text-muted" title={r.from}>
                          {r.from}
                        </span>
                        <ArrowRightIcon
                          className="h-4 w-4 shrink-0 text-muted"
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1 truncate font-medium text-fg" title={r.to}>
                          {r.to}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {total > scan.result.sample.length && (
                    <p className="text-xs text-muted">
                      {interpolate(
                        t('fixExtensions.andMore'),
                        {
                          count: total - scan.result.sample.length,
                        },
                        locale,
                      )}
                    </p>
                  )}
                </div>
              )}

              {fixError !== null && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {fixError === 'writeDisabled'
                    ? t('fixExtensions.errorWriteDisabled')
                    : t('fixExtensions.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {done || !canFix ? (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            >
              {t('fixExtensions.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('fixExtensions.cancel')}
              </button>
              <button
                type="button"
                ref={confirmRef}
                onClick={() => void onSubmit()}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {busy
                  ? t('fixExtensions.submitting')
                  : interpolate(t('fixExtensions.confirm'), { count: total }, locale)}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
